import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  Classification,
  ClassificationGroup,
  ClassificationLevel,
  ClassificationLevelClientRate,
  MinimumInput,
} from '../generated/gql-types';
import { getApolloClient } from '../lib/getApolloClient';
import {
  GET_CLASSIFICATION_GROUPS,
  GET_CLASSIFICATION_LEVEL_CLIENT_RATES,
  SET_CLASSIFICATION_LEVEL_CLIENT_RATE,
} from './classification.gql';

interface ClassificationState {
  classificationGroups: ClassificationGroup[];
  isLoading: boolean;
  classificationLevelClientRates: ClassificationLevelClientRate[];
  fetchClassificationGroups: (tenantId?: string) => Promise<void>;
  getClassificationById: (id: string) => Classification | null;
  getClassificationLevelById: (id: string) => ClassificationLevel | null;
  getClassificationGroupsByClassifications: (
    classifications: Classification[],
  ) => ClassificationGroup[];
  fetchClassificationLevelClientRates: (
    clientId: string,
  ) => Promise<ClassificationLevelClientRate[]>;
  setClassificationLevelClientRate: (
    classificationLevelId: string,
    clientId: string,
    minimums: MinimumInput[],
  ) => Promise<{
    success: boolean;
    rate?: ClassificationLevelClientRate;
    error?: string;
  }>;
  getClassificationLevelRatesForClient: (
    clientId: string,
  ) => ClassificationLevelClientRate[];
}

export const useClassificationStore = create<ClassificationState>()(
  persist(
    (set, get) => ({
      //   state
      classificationGroups: [],
      isLoading: false,
      classificationLevelClientRates: [],

      //   mutations
      fetchClassificationGroups: async (tenantId?: string) => {
        try {
          set({ isLoading: true });
          const { data } = await getApolloClient().query({
            query: GET_CLASSIFICATION_GROUPS,
            variables: { tenantId },
          });

          set({
            classificationGroups: data?.getClassificationGroups || [],
            isLoading: false,
          });
        } catch (error) {
          console.error('Error fetching classification groups:', error);
          set({ isLoading: false });
        }
      },

      fetchClassificationLevelClientRates: async (
        clientId: string,
      ): Promise<ClassificationLevelClientRate[]> => {
        try {
          const { data } = await getApolloClient().query({
            query: GET_CLASSIFICATION_LEVEL_CLIENT_RATES,
            variables: { clientId },
          });
          const rates = data?.getClassificationLevelClientRates || [];
          set(() => ({
            classificationLevelClientRates: rates,
          }));
          return rates;
        } catch (error) {
          console.error(
            'Error fetching classification level client rates:',
            error,
          );
          return [];
        }
      },

      setClassificationLevelClientRate: async (
        classificationLevelId: string,
        clientId: string,
        minimums: MinimumInput[],
      ) => {
        try {
          // Validate input
          if (!classificationLevelId?.trim()) {
            return {
              success: false,
              error: 'Classification level ID is required',
            };
          }
          if (!clientId?.trim()) {
            return { success: false, error: 'Client ID is required' };
          }
          if (!Array.isArray(minimums) || minimums.length === 0) {
            return {
              success: false,
              error: 'At least one minimum (country hash + rate) is required',
            };
          }
          const invalid = minimums.some(
            (m) =>
              typeof (m.minimum ?? undefined) !== 'number' ||
              (m.minimum as number) < 0,
          );
          if (invalid) {
            return {
              success: false,
              error: 'Each minimum must have a non-negative rate in cents',
            };
          }

          const { data } = await getApolloClient().mutate({
            mutation: SET_CLASSIFICATION_LEVEL_CLIENT_RATE,
            variables: {
              classificationLevelId,
              clientId,
              minimums,
            },
          });

          if (!data?.setClassificationLevelClientRate) {
            return {
              success: false,
              error: 'Failed to set classification level client rate',
            };
          }

          const rate = data.setClassificationLevelClientRate;

          // Update the store with the new/updated rate
          set((state) => {
            const existingRates = state.classificationLevelClientRates;
            const existingIndex = existingRates.findIndex(
              (r) =>
                r.classificationLevelId === classificationLevelId &&
                r.clientId === clientId,
            );

            if (existingIndex >= 0) {
              // Update existing rate
              const updatedRates = [...existingRates];
              updatedRates[existingIndex] = rate;
              return {
                classificationLevelClientRates: updatedRates,
              };
            } else {
              // Add new rate
              return {
                classificationLevelClientRates: [...existingRates, rate],
              };
            }
          });

          return { success: true, rate };
        } catch (error) {
          console.error(
            'Error setting classification level client rate:',
            error,
          );
          return {
            success: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };
        }
      },

      // getters
      getClassificationById: (id: string) => {
        return (
          get()
            .classificationGroups.flatMap((group) => group.classifications)
            .find((classification) => classification?.id === id) || null
        );
      },

      getClassificationLevelById: (id: string) => {
        return (
          get()
            .classificationGroups.flatMap(
              (group) => group.classifications ?? [],
            )
            .flatMap(
              (classification) => classification?.classificationLevels ?? [],
            )
            .find((level) => level?.id === id) ?? null
        );
      },

      getClassificationGroupsByClassifications: (
        classifications: Classification[],
      ) => {
        return get().classificationGroups.filter((group) =>
          group.classifications?.some((classification) =>
            classifications?.some((c) => c.id === classification?.id),
          ),
        );
      },
      getClassificationLevelRatesForClient: (clientId: string) =>
        get().classificationLevelClientRates.filter(
          (rate) => rate.clientId === clientId,
        ),
    }),
    {
      name: 'classification-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        classificationGroups: state.classificationGroups,
        classificationLevelClientRates: state.classificationLevelClientRates,
      }),
    },
  ),
);
